import {
  CLEAR_DATE_STRING,
  DepartmentSizeQueryFilter,
  SuggestionsRequest,
  type Campaign,
  type CampaignUpdate,
  type Configuration,
  type ConfigurationSuggestion,
  type ContactConfiguration,
  type EventQueryFilter,
  type NewCampaign,
  type QueryFilter,
  type SimpleCampaign,
  type TOverviewUsageResponse,
} from "@/types/campaign";
import {
  createNamedErrorType,
  LassoErrorCode,
  lassoExceptionSchema,
} from "@/types/lasso-exception";
import { api, buildQueryString } from "@/utils/api";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ActivityStatisticsParams } from "./dashboard.api";

export const listCampaignsParamsSchema = z
  .object({
    name: z.string(),
    type: z.array(z.string()),
    status: z.array(z.string()),
    state: z.array(z.string()),
    creatorId: z.array(z.string()),
    responsibleId: z.array(z.string()),
    orderBy: z.string(),
    desc: z.boolean(),
    includeStatistics: z.boolean(),
  })
  .partial();

export type ListCampaignsParams = z.infer<typeof listCampaignsParamsSchema>;

export type CreateCampaignParams = Omit<
  NewCampaign,
  | "id"
  | "lassoOrgId"
  | "creatorId"
  | "created"
  | "status"
  | "statusTimestamp"
  | "state"
  | "stateTimestamp"
  | "statistics"
  | "estimatedEndDate"
  | "estimatedRemaining"
>;

export type CampaignRunsResponse = {
  totalRuns: number;
  latestRun: CampaignRun | null;
};

export type CampaignRun = {
  id: number;
  campaignId: number;
  scheduleId: number | null;
  scheduleChangeId: number | null;
  started: string | null;
  completed: string | null;
  requestedLeads: number;
  delivered: number | null;
  duplicates: number | null;
  skipped: number | null;
  insufficientFunds: number | null;
  error: unknown | null;
};

export type CampaignRunMaxLeads = {
  max: number;
  reason: "MaxRunCount" | "NotEnoughLeads" | "NotEnoughCredits" | (string & {});
};

export const StartCampaignRunError = createNamedErrorType({
  runAlreadyInProgress: () =>
    "Der er allerede en kørsel igang. Prøv igen senere.",
  notEnoughLeads: () =>
    "Du har bedt om flere leads end der er tilgængelige for kampagnen. Reducer antallet af leads og prøv igen.",
});

export type CampaignLeadCost = {
  // Maximum credit cost per lead based on campaign settings
  leadCost: number;
  creditEstimateNextPeriod: number;
  creditEstimateRestOfPeriod: number;
};

export const campaignsApi = {
  get: (id: number, includeStatistics = false) => {
    const query = new URLSearchParams();
    if (includeStatistics) query.set("includeStatistics", "true");
    return api
      .get(`apps/sales/campaigns/${id}?${query.toString()}`)
      .json<Campaign>();
  },
  getCampaignRuns: (id: number) =>
    api.get(`apps/sales/campaigns/${id}/runs`).json<CampaignRunsResponse>(),
  getCampaignRunMaxLeads: (
    id: number,
    options: {
      deliverContacts: boolean;
      deliverContactsRequirePhone: boolean;
      deliverLiveNumber: boolean;
      completionEmail: string | null;
      /** Fresh count from /configuration/counts. If omitted, the backend counts again. */
      remainingLeads?: number;
    },
  ) =>
    api
      .post(`apps/sales/campaigns/${id}/run/max`, {
        json: options,
      })
      .json<CampaignRunMaxLeads>(),
  startCampaignRun: async (
    id: number,
    options: {
      amount: number;
      deliverContacts: boolean;
      deliverContactsRequirePhone: boolean;
      deliverLiveNumber: boolean;
      completionEmail: string | null;
      adjustAmountIfTooMany?: boolean;
    },
  ) => {
    const response = await api.post(`apps/sales/campaigns/${id}/run`, {
      json: options,
      throwHttpErrors: false,
    });
    const json = await response.json<CampaignRun>();
    if (!response.ok) {
      const { data: lassoException } = lassoExceptionSchema.safeParse(json);
      if (!lassoException) throw new Error("Unknown error");
      switch (lassoException.errorCode) {
        case LassoErrorCode.LimitReachedError:
          throw new StartCampaignRunError("runAlreadyInProgress");

        case LassoErrorCode.InvalidArgumentError:
          if (lassoException.errorMessage === "NotEnoughLeads")
            throw new StartCampaignRunError("notEnoughLeads");
          break;

        default:
          throw new Error(
            `Unhandled error code: ${lassoException.errorCode}, HTTP status: ${response.status}, message: ${lassoException.errorMessage}`,
          );
      }
    }
    return json;
  },
  list: async (params: ListCampaignsParams) => {
    return api
      .get(`apps/sales/campaigns${buildQueryString(params)}`)
      .json<Campaign[]>();
  },
  getDataFromFile: async (
    file: File,
    options?: {
      columnName?: string;
      getHeaders?: boolean;
      hasHeaders?: boolean;
    },
  ): Promise<string[]> => {
    const formData = new FormData();
    formData.append("file", file);

    const params = new URLSearchParams();
    if (options?.columnName) {
      params.set("columnName", options.columnName);
    }
    if (options?.getHeaders) {
      params.set("getHeaders", "true");
    }
    if (options?.hasHeaders !== undefined) {
      params.set("hasHeaders", options.hasHeaders ? "true" : "false");
    }

    const queryString = params.toString();
    const url = `utilities/datafromfile${queryString ? `?${queryString}` : ""}`;

    return api.post(url, { body: formData }).json<string[]>();
  },
  listSimple: () =>
    api.get("apps/sales/campaigns/simple").json<SimpleCampaign[]>(),
  update: (id: number, params: CampaignUpdate) => {
    return api
      .put(`apps/sales/campaigns/${id}`, {
        json: {
          ...params,
          chosenStartDate:
            params.chosenStartDate === null
              ? CLEAR_DATE_STRING
              : params.chosenStartDate,
          chosenEndDate:
            params.chosenEndDate === null
              ? CLEAR_DATE_STRING
              : params.chosenEndDate,
        },
      })
      .json<Campaign>();
  },
  create: (
    params: CreateCampaignParams,
    options?: {
      startNow?: boolean;
      sendNow?: boolean;
    },
  ) => {
    return api
      .post(`apps/sales/campaigns${buildQueryString(options ?? {})}`, {
        json: params,
      })
      .json<Campaign>();
  },
  delete: (id: number) => {
    return api.delete(`apps/sales/campaigns/${id}`);
  },
  fetchSuggestions: async (req: SuggestionsRequest) => {
    return await api
      .post("apps/sales/configuration/suggest", {
        json: req,
      })
      .json<ConfigurationSuggestion[]>();
  },
  calculateLeadCost: (campaign: NewCampaign) => {
    return api
      .post("apps/sales/campaigns/calculateleadcost", {
        json: campaign,
      })
      .json<CampaignLeadCost>();
  },

  getUsageData: async ({
    period,
    from,
    to,
    campaignIds,
    signal,
  }: Partial<ActivityStatisticsParams> & {
    signal?: AbortSignal;
  }): Promise<TOverviewUsageResponse> => {
    const searchParams = {
      from,
      to,
    };

    return await api
      .post(
        `apps/sales/credits/usage/${period}?from=${searchParams.from}&periodCount=${searchParams.to}`,
        {
          json: campaignIds,
          signal,
        },
      )
      .json<TOverviewUsageResponse>();
  },
};

export const getCampaignQueryOptions = (id?: number) =>
  queryOptions({
    queryKey: ["campaign", id],
    queryFn: () => {
      return campaignsApi.get(id!);
    },
    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep unused data in cache for 30 minutes
  });

export function useCampaign(id: number) {
  return useQuery(getCampaignQueryOptions(id));
}

export const getCampaignRunsQueryOptions = (id: number) =>
  queryOptions({
    queryKey: ["campaignRuns", id],
    queryFn: () => campaignsApi.getCampaignRuns(id),
  });

export const getCampaignRunMaxLeadsQueryOptions = (
  id: number,
  options: {
    deliverContacts: boolean;
    deliverContactsRequirePhone: boolean;
    deliverLiveNumber: boolean;
    completionEmail: string | null;
    remainingLeads?: number;
  },
) =>
  queryOptions({
    queryKey: ["campaignRunMaxLeads", id, options],
    queryFn: () => campaignsApi.getCampaignRunMaxLeads(id, options),
  });

export const getCampaignsQueryOptions = (params?: ListCampaignsParams) => {
  params = listCampaignsParamsSchema.strip().parse(params || {});
  if (!params.orderBy) {
    params.orderBy = "created";
    params.desc = true;
  }

  return queryOptions({
    queryKey: ["campaigns", params],
    queryFn: () => {
      return campaignsApi.list(params);
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const campaignsQueryOptions = getCampaignsQueryOptions({
  includeStatistics: true,
});

export function useCampaigns() {
  return useQuery(campaignsQueryOptions);
}

export const simpleCampaignsQueryOptions = queryOptions({
  queryKey: ["simpleCampaigns"],
  queryFn: campaignsApi.listSimple,
  staleTime: 1000 * 60 * 60, // 1 hour
  refetchOnMount: false,
  refetchOnWindowFocus: false,
});

export function useSimpleCampaigns() {
  const { data } = useQuery(simpleCampaignsQueryOptions);
  return data;
}

export function useDeleteCampaign(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) => campaignsApi.delete(id),
    onSuccess: (_, { id }) => {
      navigate({ to: "/campaigns" }).then(() => {
        queryClient.removeQueries({
          queryKey: ["campaign", id],
        });

        queryClient.setQueriesData<Campaign[]>(
          {
            queryKey: ["campaigns"],
          },
          (queryData) => {
            if (!queryData) return queryData;
            const campaignIndex = queryData.findIndex((c) => c.id === id);
            if (campaignIndex === -1) return queryData;
            const newQueryData = [...queryData];
            newQueryData.splice(campaignIndex, 1);
            return newQueryData;
          },
        );

        queryClient.setQueriesData<SimpleCampaign[]>(
          {
            queryKey: ["simpleCampaigns"],
          },
          (queryData) => {
            if (!queryData) return queryData;
            const campaignIndex = queryData.findIndex((c) => c.id === id);
            if (campaignIndex === -1) return queryData;
            const newQueryData = [...queryData];
            newQueryData.splice(campaignIndex, 1);
            return newQueryData;
          },
        );

        queryClient.invalidateQueries({
          queryKey: ["campaign-leads-overview"],
          predicate: ({ queryKey }) => {
            const [, ids = null] = queryKey;
            if (Array.isArray(ids)) {
              return ids.includes(id);
            }
            return false;
          },
        });

        options?.onSuccess?.();
      });
    },
    onError: options?.onError,
  });
}

type UpdateCampaignOptions = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export function useUpdateCampaignMutation(
  options?: UpdateCampaignOptions,
  delayMs?: number,
) {
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: number } & CampaignUpdate) =>
      campaignsApi.update(payload.id, payload),
    onMutate: async (_) => {
      await delay(delayMs || 0);
    },
    onSuccess: (campaign) => {
      queryClient.setQueryData(["campaign", campaign.id], campaign);

      const campaignsQueries = queryClient.getQueriesData<Campaign[]>({
        queryKey: ["campaigns"],
      });

      // Update "campaigns" queries containing this campaign
      for (const [queryKey, queryData] of campaignsQueries) {
        if (!queryData) continue;
        const campaignIndex = queryData.findIndex((c) => c.id === campaign.id);
        if (campaignIndex === -1) continue;
        const oldCampaign = queryData[campaignIndex];
        const newQueryData = [...queryData];

        // Remove campaign from query data if it no longer matches the filters
        const params = queryKey[1] as ListCampaignsParams | undefined;
        if (params) {
          const removeFromQuery = () => {
            newQueryData.splice(campaignIndex, 1);
            queryClient.setQueryData(queryKey, newQueryData);
          };

          if (params.status?.length) {
            const statuses = new Set(params.status);
            if (!statuses.has(campaign.status)) {
              removeFromQuery();
              continue;
            }
          }

          if (params.state?.length && campaign.state) {
            const states = new Set(params.state);
            if (!states.has(campaign.state)) {
              removeFromQuery();
              continue;
            }
          }
        }

        // Update campaign in query data, preserving statistics if not present in the update.
        newQueryData.splice(campaignIndex, 1, {
          ...campaign,
          statistics: campaign.statistics || oldCampaign.statistics,
        });

        queryClient.setQueryData(queryKey, newQueryData);
      }

      queryClient.invalidateQueries({
        queryKey: ["simpleCampaigns"],
      });

      queryClient.invalidateQueries({
        queryKey: ["campaign-leads-overview"],
        predicate: ({ queryKey }) => {
          const [, ids = null] = queryKey;
          if (Array.isArray(ids)) {
            return ids.includes(campaign.id);
          }
          return false;
        },
      });

      queryClient.invalidateQueries({
        queryKey: ["credits"],
      });

      options?.onSuccess?.();
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaign", data?.id] });
      queryClient.invalidateQueries({ queryKey: ["camapign", data?.id, true] });
    },
    onError: options?.onError,
  });
}

export const getCampaignLeadCostQueryOptions = (campaign: NewCampaign) =>
  queryOptions({
    queryKey: ["campaignLeadCost", campaign],
    queryFn: () => campaignsApi.calculateLeadCost(campaign),
  });

export function useCampaignLeadCost(campaign: NewCampaign) {
  return useQuery(getCampaignLeadCostQueryOptions(campaign));
}

export const useUsageCampaign = (
  params: Partial<ActivityStatisticsParams>,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  },
) => {
  const { enabled = true, refetchInterval } = options || {};

  return useQuery({
    queryKey: [
      "usageCampaign",
      params.period,
      params.from,
      params.to,
      params.campaignIds,
    ],
    queryFn: ({ signal }) => {
      if (params && params.period && params.from && params.to) {
        return campaignsApi.getUsageData({
          ...(params as ActivityStatisticsParams),
          signal,
        });
      }
    },

    enabled: enabled && (params?.to as number) < 3000,
    refetchInterval,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const createBaseConfiguration = ({
  baseFilters,
  contactConfiguration,
}: {
  baseFilters: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[];
  contactConfiguration: ContactConfiguration | null;
}): Configuration => ({
  name: "Guld",
  query: {
    limit: null,
    offset: 0,
    descendingOrder: false,
    orderBy: null,
    filters: baseFilters,
  },
  contactConfiguration: contactConfiguration,
  priority: 1,
  quality: 1,
  estimatedRemaining: null,
  idealLeadShare: 100,
});

export const useFileDataMutation = () => {
  return useMutation({
    mutationFn: ({
      file,
      options,
    }: {
      file: File;
      options?: {
        columnName?: string;
        getHeaders?: boolean;
        hasHeaders?: boolean;
      };
    }) => campaignsApi.getDataFromFile(file, options),
  });
};
